import BASE_URL from "../../config/api";
import React, { useState } from "react";
import { Upload, Form } from "antd";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { BsCloudUpload, BsCheckCircle, BsPlusCircle, BsTrash } from "react-icons/bs";
const { Dragger } = Upload;

const UploadZone = ({ label, uploaded, fileName, config }) => (
  <div>
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{label}</p>
    <Dragger {...config} className="!border-dashed !border-gray-300 !rounded-xl !bg-gray-50 hover:!border-[#1877F2] transition-colors">
      {fileName ? (
        <div className="py-5 flex flex-col items-center gap-1.5">
          <BsCheckCircle size={28} className="text-green-500" />
          <p className="text-xs font-semibold text-gray-700">{fileName}</p>
          <p className="text-[10px] text-gray-400">Click or drag to replace</p>
        </div>
      ) : (
        <div className="py-6 flex flex-col items-center gap-1.5">
          <BsCloudUpload size={28} className="text-gray-400" />
          <p className="text-xs font-semibold text-gray-600">Upload {label}</p>
          <p className="text-[10px] text-gray-400">Max 2MB · JPG, PNG, WebP</p>
        </div>
      )}
    </Dragger>
  </div>
);

const Dashboard = () => {
  const [formFields, setFormFields] = useState({
    frontElevation: "",
    rightElevation: "",
    leftElevation: "",
    type: "",
    category: "",
    description: "",
    refNo: "",
    price: "",
    drawing_details: [{ floor: "", details: "" }],
  });

  const [uploads, setUploads] = useState({
    front: { file: null, name: "" },
    right: { file: null, name: "" },
    left: { file: null, name: "" },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const makeUploadHandler = (key, fieldKey) => (file) => {
    setUploads((prev) => ({ ...prev, [key]: { ...prev[key], file } }));
    if (file.file.status === "done") {
      const response = file.file.response;
      if (response?.success) {
        const url = response.data[0].bucket.data.url;
        setFormFields((prev) => ({ ...prev, [fieldKey]: url }));
        setUploads((prev) => ({ ...prev, [key]: { file, name: file.file.name } }));
        toast.success(`${file.file.name} uploaded`);
      } else {
        setUploads((prev) => ({ ...prev, [key]: { file: null, name: "" } }));
        toast.error("Upload failed");
      }
    } else if (file.file.status === "error") {
      setUploads((prev) => ({ ...prev, [key]: { file: null, name: "" } }));
      toast.error("Upload failed — network issue");
    }
  };

  const uploadConfig = (onChange) => ({
    name: "file",
    maxCount: 1,
    action: `${BASE_URL}/bucket/upload`,
    headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` },
    onChange,
    showUploadList: false,
  });

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleDetailChange = (e, index, key) => {
    const updated = [...formFields.drawing_details];
    updated[index][key] = e.target.value;
    setFormFields((prev) => ({ ...prev, drawing_details: updated }));
  };

  const addDetail = () => {
    setFormFields((prev) => ({
      ...prev,
      drawing_details: [...prev.drawing_details, { floor: "", details: "" }],
    }));
  };

  const removeDetail = (index) => {
    setFormFields((prev) => ({
      ...prev,
      drawing_details: prev.drawing_details.filter((_, i) => i !== index),
    }));
  };

  const submit = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${BASE_URL}/drawing/add-drawing`, formFields, {
        headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` },
      });
      if (response.status === 200) {
        toast.success("Drawing added successfully");
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Submission failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1877F2]/30 focus:border-[#1877F2] transition-colors";
  const selectClass = `${inputClass} appearance-none`;
  const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";

  return (
    <div>
      <ToastContainer />
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Add New Drawing</h1>
        <p className="text-gray-500 text-sm mt-1">Upload elevation images and fill in the plan details.</p>
      </div>

      <Form onFinish={submit} className="space-y-6 max-w-3xl">

        {/* Elevations */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-5">Elevation Images</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <UploadZone
              label="Front Elevation"
              fileName={uploads.front.name}
              config={uploadConfig(makeUploadHandler("front", "frontElevation"))}
            />
            <UploadZone
              label="Right Elevation"
              fileName={uploads.right.name}
              config={uploadConfig(makeUploadHandler("right", "rightElevation"))}
            />
            <UploadZone
              label="Left Elevation"
              fileName={uploads.left.name}
              config={uploadConfig(makeUploadHandler("left", "leftElevation"))}
            />
          </div>
        </div>

        {/* Plan Details */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-5">Plan Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Type</label>
              <select name="type" value={formFields.type} onChange={handleFieldChange} className={selectClass}>
                <option value="">Select type</option>
                <option value="bungalow">Bungalow</option>
                <option value="duplex">Duplex</option>
                <option value="flat">Flat</option>
                <option value="nonResidential">Non-Residential</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Category</label>
              <select name="category" value={formFields.category} onChange={handleFieldChange} className={selectClass}>
                <option value="">Select category</option>
                <option value="2-bedroom">2 Bedroom</option>
                <option value="3-bedroom">3 Bedroom</option>
                <option value="4-bedroom">4 Bedroom</option>
                <option value="5-bedroom">5 Bedroom</option>
                <option value="6-bedroom">6+ Bedroom</option>
                <option value="nonResidential">Non-Residential</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Description</label>
              <input
                required
                type="text"
                name="description"
                value={formFields.description}
                placeholder="e.g. 4 Bedroom Bungalow with Study Room"
                onChange={handleFieldChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Reference Number</label>
              <input
                required
                type="text"
                name="refNo"
                value={formFields.refNo}
                placeholder="e.g. PS-B-001"
                onChange={handleFieldChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Price (₦)</label>
              <input
                required
                type="text"
                name="price"
                value={formFields.price}
                placeholder="e.g. 150000"
                onChange={handleFieldChange}
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Floor Details */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Floor Details</h2>
            <button
              type="button"
              onClick={addDetail}
              className="flex items-center gap-1.5 text-sm font-semibold text-[#1877F2] hover:text-[#0e5fc7] transition-colors"
            >
              <BsPlusCircle size={16} /> Add Floor
            </button>
          </div>

          <div className="space-y-4">
            {formFields.drawing_details.map((field, index) => (
              <div key={index} className="flex gap-3 items-start">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1877F2]/10 text-[#1877F2] flex items-center justify-center text-xs font-bold mt-3">
                  {index + 1}
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder={`Floor name (e.g. Ground Floor)`}
                    value={field.floor}
                    onChange={(e) => handleDetailChange(e, index, "floor")}
                    className={inputClass}
                  />
                  <textarea
                    rows={1}
                    placeholder="Details (e.g. 2 bedrooms, living room)"
                    value={field.details}
                    onChange={(e) => handleDetailChange(e, index, "details")}
                    className={`${inputClass} resize-none`}
                  />
                </div>
                {formFields.drawing_details.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDetail(index)}
                    className="mt-3 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                  >
                    <BsTrash size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <Form.Item style={{ marginBottom: 0 }}>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-[#1877F2] hover:bg-[#0e5fc7] text-white font-bold px-10 py-3 rounded-xl transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Submitting…" : "Add Drawing"}
          </button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default Dashboard;
